import {
  ActionIcon,
  Button,
  Divider,
  Group,
  List,
  ListItem,
  NativeSelect,
  Text,
  TextInput,
} from '@mantine/core';
import { formList, useForm } from '@mantine/form';
import { useState } from 'react';
import { Trash } from 'tabler-icons-react';
import { showErrorNotification, showSuccessNotification } from 'utils/ui-toaster';
import { Connector } from 'utils/connectors';

type FormSubmitValues = {
  name: string;
  type: string;
  host: string;
  headers: { key: string; value: string }[];
  status: string;
  description?: string;
};
const newHeaderFields = {
  key: '',
  value: '',
};

export const ConnectorForm = (
  props: { onSubmit?: (values: FormSubmitValues) => Promise<any> } & (
    | {
        action: 'CREATE';
      }
    | {
        action: 'UPDATE';
        connector: Connector;
      }
  ),
) => {
  const { action, onSubmit } = props;
  const connector = action === 'UPDATE' ? props.connector : undefined;
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm({
    initialValues: {
      name: connector?.name ?? '',
      type: 'REST',
      headers: formList(
        connector?.headers?.length
          ? connector.headers.map(node => ({ key: node?.key ?? '', value: node?.value ?? '' }))
          : [newHeaderFields],
      ),
      host: connector?.host ?? '',
      status: 'Active',
      description: connector?.description ?? '',
    },
  });

  const handleSubmit = values => {
    setIsLoading(true);
    onSubmit(values)
      .then(() => {
        showSuccessNotification(
          `Connector was ${action === 'UPDATE' ? 'updated' : 'added'} successfully.`,
        );

        if (action === 'UPDATE') {
          return;
        }

        form.reset();
      })
      .catch(() =>
        showErrorNotification(`Failed to ${action === 'UPDATE' ? 'update' : 'add'} a connector.`),
      )
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <TextInput label="Connector Name" {...form.getInputProps('name')} required />
      <List withPadding listStyleType="none">
        <Text size="sm" style={{ margin: '8px 0px' }}>
          Headers
        </Text>

        {form.values.headers.map((_, index) => (
          <ListItem>
            <Group key={index} mt="xs">
              <TextInput
                placeholder="Header Key"
                sx={{ flex: 1 }}
                {...form.getListInputProps('headers', index, 'key')}
              />
              <TextInput
                placeholder="Header Value"
                sx={{ flex: 1 }}
                {...form.getListInputProps('headers', index, 'value')}
              />
              <ActionIcon
                style={{ display: form.values.headers.length > 1 ? 'block' : 'none' }}
                color="red"
                variant="hover"
                onClick={() => form.removeListItem('headers', index)}
              >
                <Trash size={16} />
              </ActionIcon>
            </Group>
            <Divider style={{ margin: '10px 0' }} />
          </ListItem>
        ))}
        <Button
          style={{ marginBottom: '10px' }}
          color="dark"
          size="xs"
          onClick={() => {
            form.addListItem('headers', newHeaderFields);
          }}
        >
          Add Header
        </Button>
      </List>
      <TextInput label="Host" {...form.getInputProps('host')} required />
      <TextInput label="Description" {...form.getInputProps('description')} />
      <Group position="center" mt="md">
        <Button loading={isLoading} color="dark" type="submit">
          {action === 'CREATE' ? `Create` : 'Update'}
        </Button>
      </Group>
    </form>
  );
};
